import React, { FC, useEffect, useState } from 'react';
import Popup from 'components/shared/Popups/Popup/Index';
import ConfirmPopup from 'components/shared/Popups/Confirm/Index';
import { useSelector } from 'react-redux';
import {
  IworkExperience,
  IworkExperienceData
} from 'core/interface/workExperience';
import { RootState } from 'redux/store';
import { Form, Formik, FormikHelpers } from 'formik';
import { initialValues, validation } from './form';
import DropDown from 'components/Dropdown';
import { jobContracts } from 'core/constants/workExperience';
import FormField from 'components/FormField/Index';
import { InputType } from 'core/enum/inputType';
import { months, years } from 'core/constants/aboutMePopUpConstants';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Checkboxes from 'components/shared/Checkbox/Index';
import Button from 'components/Button/Index';
import DescriptionCard from 'components/shared/DescriptionCard/Index';
import ActionButton from 'components/ActionButton/Index';
import { ProfileService } from 'services/profile/profile';
import { ISchool, IndustriesData } from 'core/interface/education-side';
import { JobService } from 'services/jobs/job';
import { IWorkExperienceBody, IWorkExperiences } from 'core/interface/work-experience-side';
import { IIndustry } from 'core/interface/companies';
import { resolveIndustryByName, getIndustryDisplayName } from 'utils/industry';
import { ExperienceService } from 'services/experience/experienceService';
import { IMe } from 'core/interface/me';
import { isValidURL } from 'helpers/isValidURL';
import { endMonthMinValue, getDateFormatter } from 'helpers/dateConverter';
import { contractTypes } from 'core/constants/workType';
import { ILocationRedux } from 'core/interface/education';
import { useDebounce } from 'helpers/useDebounce';

import './Index.scss';

const WorkExperiencePopup: FC<IworkExperience> = ({ t }): JSX.Element => {
  const { industry: industries } = useSelector(
    (selector: RootState) => selector.industries
  );
  const { experience } = useSelector(
    (state: RootState) => state.workExperiences
  );
  const [initialValue, setInitialValue] = useState(initialValues);
  const [showForm, setShowForm] = useState<boolean>(
    () => !experience?.length
  );
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const me = useSelector((state: IMe) => state.user).user;
  const [timeoutFunc, setTimeoutFunc] = useState<NodeJS.Timeout | null>(null);

  const cities = useSelector(
    (selector: ILocationRedux) => selector.locations?.cities || []
  );
  const [locationQuery, setLocationQuery] = useState<string>('');
  const debouncedLocationQuery = useDebounce(locationQuery, 500);

  useEffect(() => {
    if (!experience?.length) {
      setIsEdit(false);
      setInitialValue(initialValues);
      setShowForm(true);
    }
  }, [experience?.length]);

  useEffect(() => {
    if (debouncedLocationQuery) {
      getCities(debouncedLocationQuery);
    }
  }, [debouncedLocationQuery]);

  const handleSubmit = async (
    values: IworkExperienceData,
    actions: FormikHelpers<IworkExperienceData>
  ): Promise<void> => {
    setLoading(true);

    // Format website URL to include https:// if not already present
    const websiteUrl = values.company_website
      ? values.company_website.match(/^(https?|ftp):\/\//)
        ? values.company_website
        : `https://${values.company_website}`
      : values.company_website;

    if (!isValidURL(websiteUrl)) {
      actions.setErrors({ company_website: 'Invalid URL' });
      setLoading(false);
      return;
    }

    const industry = resolveIndustryByName(values.industry, industries);
    if (!industry) {
      actions.setFieldError('industry', t('required'));
      setLoading(false);
      return;
    }

    try {
      let saved: unknown = null;
      if (!isEdit) {
        saved = await setExperience(
          {
            ...values,
            company_website: websiteUrl
          },
          industry
        );
      } else {
        const body: IWorkExperienceBody = {
          company: values.company_name,
          contract: +values.work_type,
          description: values.description,
          start: `${values.start.year}-${values.start.month}-01`,
          industry,
          location: values.location,
          end:
            values.finish && Object.values(values.finish).some((e) => e === '')
              ? null
              : values.finish
                ? `${values.finish.year}-${values.finish.month}-02`
                : null,
          title: values.job_title,
          website: websiteUrl
        };
        saved = await ExperienceService.updateExperience(
          me.candidate_id,
          initialValue.id,
          body
        );
      }
      if (saved) {
        resetExperienceForm(actions);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetExperienceForm = (
    actions?: FormikHelpers<IworkExperienceData>
  ): void => {
    actions?.resetForm({ values: initialValues });
    setInitialValue(initialValues);
    setIsEdit(false);
    setShowForm(false);
  };

  const openAddForm = (): void => {
    setIsEdit(false);
    setInitialValue(initialValues);
    setShowForm(true);
  };

  const setExperience = async (
    values: IworkExperienceData,
    industry: IIndustry
  ): Promise<IWorkExperiences[] | null> => {
    // The website URL is already formatted in handleSubmit function
    const body: IWorkExperienceBody = {
      company: values.company_name,
      contract: +values.work_type,
      description: values.description,
      start: `${values.start.year}-${values.start.month}-01`,
      industry,
      location: values.location,
      end:
        values.finish && Object.values(values.finish).some((e) => e === '')
          ? null
          : values.finish
            ? `${values.finish.year}-${values.finish.month}-${values.finish.day}`
            : null,
      title: values.job_title,
      website: values.company_website // This is already formatted in handleSubmit
    };
    if (values.ongoing) {
      body.end = null;
    }
    return ExperienceService.setExperience(me.candidate_id, body);
  };

  const handleRemove = (id: number): void => {
    setPendingDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = (): void => {
    if (pendingDeleteId !== null) {
      ExperienceService.removeExperience(me.candidate_id, pendingDeleteId);
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const cancelDelete = (): void => {
    setDeleteConfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleEdit = (id: number): void => {
    const foundExperience = experience?.find(
      (exp: IWorkExperiences) => exp.id === id
    );
    if (foundExperience) {
      const body: IworkExperienceData = {
        company_name: foundExperience.company,
        company_website: foundExperience.website,
        description: foundExperience.description,
        id: foundExperience.id,
        job_title: foundExperience.title,
        industry: getIndustryDisplayName(foundExperience.industry),
        work_type: String(
          jobContracts.find(
            (contract) => contract.value === foundExperience.contract
          )?.value
        ),
        start: {
          month: getDateFormatter(foundExperience.start, 'MM'),
          year: getDateFormatter(foundExperience.start, 'YYYY'),
          day: getDateFormatter(foundExperience.start, 'D')
        },
        finish: {
          month: getDateFormatter(foundExperience.end, 'MM'),
          year: getDateFormatter(foundExperience.end, 'YYYY'),
          day: getDateFormatter(foundExperience.end, 'D')
        },
        location: {
          display_name: foundExperience.location.display_name,
          id: foundExperience.location.id
        },
        ongoing: !foundExperience.end
      };
      setInitialValue(body);
    }
    setIsEdit(true);
    setShowForm(true);
  };

  useEffect(() => {
    getIndustries('', false);
  }, []);

  const getIndustries = async (
    search: string = '',
    bounce = true
  ): Promise<void> => {
    if (bounce) {
      if (timeoutFunc) {
        clearTimeout(timeoutFunc);
      }
      const timeout = setTimeout(() => {
        ProfileService.getIndustries(search);
      }, 500);
      setTimeoutFunc(timeout);
    } else {
      ProfileService.getIndustries(search);
    }
  };

  const getCities = (q: string = ''): void => {
    JobService.getLocations({
      q
    });
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  const handleLocationChange = (
    e: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setLocationQuery(e);
    if (cities.find((city) => city.display_name === e)) {
      setFieldValue('location', {
        id: cities.find((city) => city.display_name === e)?.id,
        display_name: cities.find((city) => city.display_name === e)
          ?.display_name
      });
    }
  };

  return (
    <div className="wexperience scrollable">
      {deleteConfirmOpen && (
        <Popup
          className="popUp"
          title={t('Delete Work Experience')}
          onClose={cancelDelete}
        >
          <ConfirmPopup
            description={t('Are you sure you want to delete this work experience?')}
            onApprove={confirmDelete}
            onCancel={cancelDelete}
            approveTitle={t('Yes, Delete')}
            cancelTitle={t('Cancel')}
            t={t}
          />
        </Popup>
      )}
      {!showForm &&
        experience?.map((item: IWorkExperiences, index: number) => (
            <DescriptionCard
              handleRemove={(e: number) => handleRemove(e)}
              handleEdit={(e: number) => handleEdit(e)}
              key={index}
              id={item.id}
              description={item.description}
              title={item.title}
              year_from={item.start}
              year_to={item.end}
              details={[
                {
                  icon: 'location.svg',
                  title: item.location.display_name
                },
                {
                  icon: 'work-progress.svg',
                  title:
                    (jobContracts?.find(
                      (contract) => contract.value === item.contract
                    )?.to_show || '') + ''
                },
                { icon: 'engineering.svg', title: getIndustryDisplayName(item.industry) }
              ]}
            />
          ))}

      {!showForm && (
        <div onClick={openAddForm} className="add-experience">
          <ActionButton
            className="action-add"
            onClick={openAddForm}
            iconName="plus.svg"
          />

          <span className="add-experience-title">
            {t('Add Work Experience')}
          </span>
        </div>
      )}

      {showForm && (
      <div className="wexperience__add">
        <Formik
          key={isEdit ? `edit-${initialValue.id}` : 'create'}
          enableReinitialize
          onSubmit={(values, actions) => handleSubmit(values, actions)}
          initialValues={initialValue}
          validationSchema={validation(t)}
        >
          {({
            setFieldValue,
            isValid,
            values,
            errors,
            touched,
            setFieldTouched
          }) => {
            return (
              <Form>
                <div className="field">
                  <span className="label">Industry</span>
                  <Autocomplete
                    className="inputs"
                    fieldName="industry"
                    value={values.industry}
                    options={
                      industries?.map((item: ISchool) => ({
                        id: item.id,
                        title: item.name
                      })) || []
                    }
                    placeholder="Industry"
                    show_location={false}
                    onInputChange={(e) => {
                      getIndustries(e);
                      setFieldValue(
                        'industry',
                        industries?.find((ind) => ind.name === e)?.name
                      );
                    }}
                    values={industries}
                    onFocus={(e) => {
                      getIndustries(e);
                    }}
                  />
                </div>

                <div className="job">
                  <div className="left">
                    <div className="field">
                      <span className="label">{t('Job Title')}</span>
                      <FormField
                        type={InputType.text}
                        fieldName="job_title"
                        placeholder={t('Job title')}
                        inputClassName="input"
                      />
                    </div>
                    <div className="field">
                      <span className="label">{t('Company Website')}</span>
                      <FormField
                        type={InputType.text}
                        fieldName="company_website"
                        placeholder={t('Company Website')}
                        inputClassName="input"
                      />
                    </div>
                  </div>
                  <div className="right">
                    <div className="field">
                      <span className="label">{t('Company name')}</span>
                      <FormField
                        type={InputType.text}
                        fieldName="company_name"
                        placeholder={t('Company Name')}
                        inputClassName="input"
                      />
                    </div>
                    <div className="field">
                      <span className="label">{t('Location')}</span>
                      <Autocomplete
                        className="inputs"
                        fieldName="location"
                        value={values.location.display_name}
                        options={
                          cities.map((item: IndustriesData) => ({
                            id: item.id,
                            title: item.display_name
                          })) || []
                        }
                        placeholder="Location"
                        show_location={false}
                        onInputChange={(e) => {
                          handleLocationChange(e, setFieldValue);
                        }}
                        values={cities}
                        loadMore={handleLoadMoreCities}
                      />
                    </div>
                  </div>
                </div>

                <div className="field">
                  <span className="label">{t('Description')}</span>
                  <FormField
                    type={InputType.text}
                    fieldName="description"
                    placeholder={t('Description')}
                    inputClassName="input"
                  />
                </div>
                <div className="field">
                  <span className="label">{t('Work type')}</span>
                  <DropDown
                    error={
                      errors.work_type && touched.work_type
                        ? errors.work_type
                        : ''
                    }
                    placeholder={t('Work type')}
                    defaultValue={values.work_type}
                    dropdownClassName="dropdown"
                    items={contractTypes}
                    selectedValue={(value) => {
                      setFieldTouched('work_type', true);
                      setFieldValue('work_type', value);
                    }}
                  />
                </div>
                <div className="bottom">
                  <div className="left" style={{ gap: '20px' }}>
                    <div className="field">
                      <span className="label">{t('Start')}</span>
                      <DropDown
                        error={
                          errors.start?.month && touched.start?.month
                            ? errors.start?.month
                            : ''
                        }
                        placeholder="Month"
                        defaultValue={values.start.month}
                        dropdownClassName="dropdown month"
                        items={months}
                        selectedValue={(value) => {
                          setFieldTouched('start.month', true);
                          setFieldValue('start.month', value);
                        }}
                      />
                    </div>
                    <DropDown
                      error={
                        errors.start?.year && touched.start?.year
                          ? errors.start?.year
                          : ''
                      }
                      placeholder="Year"
                      defaultValue={values.start.year}
                      dropdownClassName="dropdown year"
                      items={years}
                      selectedValue={(value) => {
                        setFieldTouched('start.year', true);
                        setFieldValue('start.year', value);
                      }}
                    />
                  </div>
                  <div
                    className={`left ${values.ongoing ? 'disabled' : ''}`}
                    style={{ gap: '20px' }}
                  >
                    <div className="field">
                      <span className="label">{t('Finish')}</span>
                      <DropDown
                        dropdownClassName="dropdown month"
                        placeholder="Month"
                        defaultValue={values.finish && values.finish.month}
                        items={months}
                        maxRange={endMonthMinValue(
                          values.start.year,
                          values.start.month,
                          values.finish?.year
                        )}
                        selectedValue={(value) => {
                          setFieldValue('finish.month', value);
                        }}
                      />
                    </div>
                    <DropDown
                      dropdownClassName="dropdown year"
                      placeholder="Year"
                      items={years}
                      maxRange={+values.start.year}
                      defaultValue={values.finish && values.finish.year}
                      selectedValue={(value) => {
                        setFieldValue('finish.year', value);
                      }}
                    />
                  </div>
                </div>
                <div className="field">
                  <Checkboxes
                    onArrayChange={(e) => {
                      if (Array.isArray(e)) {
                        setFieldValue('ongoing', e[0].isChecked);
                      }
                    }}
                    checkboxes={[
                      {
                        label: 'Ongoing',
                        isChecked: values.ongoing
                      }
                    ]}
                  />
                </div>
                <Button
                  isLoading={loading}
                  t={t}
                  title={`Save`}
                  type="submit"
                  className={`save ${!isValid ? 'invalid-btn' : ''}`}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
      )}
    </div>
  );
};

export default WorkExperiencePopup;
